import { NavLink } from 'react-router-dom';
import { Building2, Calendar, CreditCard, LayoutDashboard, Menu, Users } from 'lucide-react';
import { useUiStore } from '@/stores/ui.store';
import { useAuthStore } from '@/stores/auth.store';
import { cn } from '@/lib/utils';

/**
 * Bottom tab bar shown on mobile/tablet (`< lg`). The first 4 entries are
 * NavLinks to the most-used routes; the 5th opens the full navigation drawer.
 *
 * Layout assumes the parent <main> already has `pb-20` on `< lg` so the
 * content doesn't hide behind the bar. iOS safe-area is handled with
 * `pb-[env(safe-area-inset-bottom)]`.
 */
export function MobileBottomNav() {
  const setMobileDrawer = useUiStore((s) => s.setMobileDrawer);
  // El superadministrador usa el sistema entero para probarlo, así que ve las
  // mismas pestañas; "Empresas" le sustituye el Hub, que sin empresa propia no
  // tiene nada que mostrar.
  const isSuperAdmin = useAuthStore((s) => s.user?.isSuperAdmin ?? false);

  return (
    <nav
      className={cn(
        'fixed bottom-0 left-0 right-0 z-30 border-t border-border bg-background/95 backdrop-blur lg:hidden',
        'pb-[env(safe-area-inset-bottom)]',
      )}
      aria-label="Navegación principal"
    >
      <div className="mx-auto flex max-w-screen-md">
        {isSuperAdmin
          ? <Tab to="/admin" icon={Building2} label="Empresas" end />
          : <Tab to="/dashboard" icon={LayoutDashboard} label="Hub" end />}
        <Tab to="/agenda"    icon={Calendar}   label="Agenda" />
        <Tab to="/customers" icon={Users}      label="Clientes" />
        <Tab to="/pos"       icon={CreditCard} label="POS" />
        <button
          onClick={() => setMobileDrawer(true)}
          className={cn(
            'flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition-colors',
            'min-h-[56px] text-muted-foreground hover:text-foreground active:bg-muted/40',
          )}
          aria-label="Abrir menú"
        >
          <Menu className="h-5 w-5" />
          Más
        </button>
      </div>
    </nav>
  );
}

function Tab({
  to, icon: Icon, label, end,
}: {
  to: string;
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  end?: boolean;
}) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) =>
        cn(
          'flex flex-1 flex-col items-center gap-0.5 py-2 text-[10px] font-medium transition-colors min-h-[56px] active:bg-muted/40',
          isActive ? 'text-primary' : 'text-muted-foreground',
        )
      }
    >
      <Icon className="h-5 w-5" />
      {label}
    </NavLink>
  );
}
